import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin, getDocumentsOrigin } from "@utils/apiOrigin";

export interface RiskAnalysisResponse {
  success: boolean;
  message: string;
  analysis: {
    document_type: string;
    analysis_date: string;
    facility_info: {
      industry: string;
      facility_type: string;
    };
    incident_summary: {
      narrative: string;
      incident_type: string;
      clinical_classification: string;
      date: string;
      location: string;
      description: string;
      parties_involved: string[];
      injuries_damages: string;
      environmental_factors: string;
      reporting_timeliness: string;
      documentation_completeness: string;
    };
    compliance_deficiencies: Array<{
      category: string;
      requirement: string;
      regulatory_source: string;
      specific_regulation_citation: string;
      official_url: string;
      deficiency_description: string;
      risk_level: string;
      associated_risks: string[];
    }>;
    potential_penalties: {
      penalty_table: Array<{
        regulator: string;
        fine_range: string;
        estimated_exposure: string;
        notes: string;
      }>;
      penalty_charts: {
        estimated_penalty_exposure: {
          min: string;
          max: string;
          most_likely: string;
        };
        estimated_corrective_investment: {
          immediate: string;
          long_term: string;
          total: string;
        };
      };
    };
    plan_of_correction: {
      action_table: Array<{
        action: string;
        priority: string;
        timeline: string;
        responsible: string;
      }>;
      documentation_improvements: Array<{
        improvement: string;
        implementation_steps: string[];
        timeline: string;
      }>;
      training_requirements: Array<{
        training_topic: string;
        target_audience: string;
        frequency: string;
        duration: string;
      }>;
      system_improvements: Array<{
        system: string;
        improvement: string;
        implementation_cost: string;
        timeline: string;
      }>;
      corrective_costs: {
        immediate_costs: string;
        long_term_costs: string;
        total_estimated_cost: string;
      };
      monitoring_tracking: Array<{
        metric: string;
        frequency: string;
        responsible_party: string;
        reporting_method: string;
      }>;
    };
    risk_score: {
      overall_score: number;
      score_breakdown: {
        documentation: number;
        compliance: number;
        safety: number;
        financial_risk: number;
      };
      risk_level: string;
      issues_count: number;
      actions_count: number;
      justification: string;
    };
    executive_summary: string;
    recommendations: {
      recommendation_table: Array<{
        recommendation: string;
        priority: string;
        rationale: string;
        estimated_cost: string;
        expected_outcome: string;
      }>;
    };
  };
  metadata: {
    filename: string;
    file_size: number;
    document_type: string;
    analysis_timestamp: string;
  };
  /** Global administrators only — last LLM prompt per analysis section. */
  llmPromptsBySection?: Record<string, string>;
  llmInvokeWallSecondsBySection?: Record<string, number>;
}

export interface AnalyzeDocumentPayload {
  file: File;
  document_type: "survey_document" | "incident_report";
  industry: string;
  analysis_type: string;
  facility_id?: string;
}

// Survey Report interfaces
export interface SurveyReportItem {
  tag: string; // e.g., "J0042"
  title: string;
  type: string; // e.g., "Rural Health Clinic | G RHC"
  description: string;
  cfrCitation: string;
  version: string;
  summary?: string;
  procedures: string[];
  requirements: string[];
  authority: {
    jurisdiction: string;
    instrument: string;
    citation: string;
  };
}

export interface SurveyReportResponse {
  success: boolean;
  message: string;
  facility: {
    name: string;
    location: string;
  };
  lastUpdated: string;
  reports: SurveyReportItem[];
  metadata: {
    totalReports: number;
    facilityType: string;
    surveyDate?: string;
  };
}

export interface UploadSurveyDocumentPayload {
  file: File;
  facilityId?: string;
  documentType: "survey_report" | "compliance_document";
}

export type SurveyJobStatus = "pending" | "running" | "complete" | "failed";

export interface SurveyJobStartResponse {
  job_id: string;
  status: SurveyJobStatus;
  poll_url: string;
}

/** Result body from analyze-survey (sync or async job complete). */
export interface AnalyzeSurveyResult {
  tags?: string[];
  count?: number;
  deficiency_descriptions?: Array<{ tag: string; description: string }>;
  survey_date?: string | null;
  facility_type?: string | null;
  full_analysis?: Record<string, unknown>;
  llmPromptsBySection?: Record<string, string>;
  llmInvokeWallSecondsBySection?: Record<string, number>;
  [key: string]: unknown;
}

export interface SurveyJobStatusResponse {
  job_id: string;
  status: SurveyJobStatus;
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  wall_sec?: number | null;
  result?: AnalyzeSurveyResult | null;
}

export type DocumentJobStatus = SurveyJobStatus;

export interface DocumentJobStartResponse {
  job_id: string;
  status: DocumentJobStatus;
  poll_url: string;
}

export interface DocumentJobStatusResponse {
  job_id: string;
  status: DocumentJobStatus;
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  wall_sec?: number | null;
  result?: Record<string, unknown> | null;
}

// --- SOM Search API (rule lookup) ---
export interface SomSearchPayload {
  auth_token?: string;
  capture_test_data?: boolean;
  category: string; // e.g. "VectorStores_usa_healthcare_manual_som107ap_g_rhc"
  customer_id: string; // e.g. "1"
  org_id?: string;
  org_name?: string; // e.g. "test"
  query: string; // e.g. "J0042"
  snapshots_size?: number; // e.g. 20
  user_persona?: string; // e.g. "survey_analyst"
}

export interface SomSearchResponse {
  result: string;
  structured?: any;
  sources?: any[];
  chunks_retrieved?: number;
  total_characters?: number;
}

/** Per uploaded file: extraction outcome before / alongside LLM analysis. */
export type EmployeeAgreementFileResult = {
  filename: string;
  status:
    | "processed"
    | "empty"
    | "failed"
    | "queued"
    | "extracting";
  error?: string | null;
  /** Raw extracted text (OCR / parser output) for QA; omitted while still processing. */
  extracted_text?: string | null;
  /** Synthetic rows: server hinted PDF may take OCR path. */
  pdf_may_ocr?: boolean;
  ocr_page?: number | null;
  ocr_total_pages?: number | null;
};

/** Optional fields on SSE `file_extracting` after extraction starts. */
export type FileExtractingMeta = {
  pdf?: boolean;
  extractionHint?: string;
};

export type FilePageProgressEvent = {
  filename: string;
  page: number;
  totalPages: number;
};

export class EmployeeAgreementRequestError extends Error {
  readonly fileResults?: EmployeeAgreementFileResult[];

  constructor(message: string, fileResults?: EmployeeAgreementFileResult[]) {
    super(message);
    this.name = "EmployeeAgreementRequestError";
    this.fileResults = fileResults;
  }
}

/** Physician employment agreement analysis (API v2 JSON shape) */
export interface EmployeeAgreementAnalysisResponse {
  success: boolean;
  /** Present on live API responses; omitted when rehydrating from saved history. */
  message?: string;
  analysis: Record<string, unknown>;
  metadata: {
    filename?: string;
    filenames?: string[];
    file_count?: number;
    file_size?: number;
    employee_type?: string;
    analysis_timestamp?: string;
    file_results?: EmployeeAgreementFileResult[];
  };
}

export class PayorContractRequestError extends Error {
  readonly fileResults?: EmployeeAgreementFileResult[];

  constructor(message: string, fileResults?: EmployeeAgreementFileResult[]) {
    super(message);
    this.name = "PayorContractRequestError";
    this.fileResults = fileResults;
  }
}

/** Payer contract advantage analysis (validated JSON root from API). */
export interface PayorContractAnalysisResponse {
  success: boolean;
  message?: string;
  analysis: Record<string, unknown>;
  metadata: {
    filename?: string;
    filenames?: string[];
    file_count?: number;
    file_size?: number;
    analysis_timestamp?: string;
    file_results?: EmployeeAgreementFileResult[];
    country?: string;
    states?: string;
    facility_types?: string;
  };
}

/** Same ordering as server `_sorted_multifile_uploads_by_filename` (case-insensitive filename, stable). */
export function sortMultifileAnalysisUploadsByName(files: File[]): File[] {
  return [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
}

/** Alias for payor contract UI; same sort as physician/employee agreement uploads. */
export const sortPayorContractFilesForAnalysis = sortMultifileAnalysisUploadsByName;

const BASE_URL = getApiOrigin();
const DOCUMENTS_BASE_URL = getDocumentsOrigin();
/** Async incident analyze-document job (ACA consumption ingress ~240s safe). */
const ANALYZE_DOCUMENT_ASYNC_ENDPOINT =
  "/api/v1/risk-analysis/analyze-document/async";
const ANALYZE_DOCUMENT_JOB_ENDPOINT =
  "/api/v1/risk-analysis/analyze-document/jobs";
const SOM_SEARCH_URL_ENDPOINT = "/api/v1/som-search";
/** Async survey job (ACA consumption ingress ~240s safe). */
const ANALYZE_SURVEY_ASYNC_ENDPOINT =
  "/api/v1/risk-analysis/analyze-survey/async";
const ANALYZE_SURVEY_JOB_ENDPOINT =
  "/api/v1/risk-analysis/analyze-survey/jobs";
const SURVEY_POLL_INTERVAL_MS = 3000;
const SURVEY_POLL_MAX_ATTEMPTS = 200; // ~10 minutes
const DOCUMENT_POLL_INTERVAL_MS = SURVEY_POLL_INTERVAL_MS;
const DOCUMENT_POLL_MAX_ATTEMPTS = SURVEY_POLL_MAX_ATTEMPTS;
/** Employment agreements analyze on unicomply-documents (Document Intelligence). */
const ANALYZE_EMPLOYEE_AGREEMENT_STREAM_ENDPOINT =
  "/api/v1/document-intelligence/analyze-employment-package-stream";
const ANALYZE_PAYOR_CONTRACT_STREAM_ENDPOINT =
  "/api/v1/risk-analysis/analyze-payor-contract-stream";
const SURVEY_REPORT_ENDPOINT = "/api/v1/survey-reports";
export const RISK_ANALYSIS_MAX_UPLOAD_BYTES = 30 * 1024 * 1024;

function resolveHubOrgContext(): { orgId: string; orgName: string } {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      return { orgId: "hub", orgName: "" };
    }
    const user = JSON.parse(raw) as Record<string, unknown>;
    const orgId = String(
      user.org_id ?? user.organization_id ?? user.orgId ?? "hub",
    );
    const orgName = String(
      user.org_name ?? user.organization_name ?? user.orgName ?? "",
    );
    return { orgId: orgId || "hub", orgName };
  } catch {
    return { orgId: "hub", orgName: "" };
  }
}

function appendEmploymentPackageFormFields(
  formData: FormData,
  facilityId: string,
  employeeType: string,
): void {
  const { orgId, orgName } = resolveHubOrgContext();
  formData.append("facility_id", facilityId);
  formData.append("employee_type", employeeType);
  formData.append("org_id", orgId);
  if (orgName) {
    formData.append("org_name", orgName);
  }
  formData.append("vertical", "healthcare");
  formData.append("role", "Compliance Officer");
  formData.append("entity", orgName || "UniComply");
  formData.append("address", "Not provided");
  formData.append("country", "USA");
  formData.append("language", "en-us");
  formData.append("workflow_family", "physician_provider_agreements");
  formData.append("document_type_id", "physician_employment_agreement");
  formData.append("operation", "inbound_analysis");
}

export function formatRiskAnalysisMaxUploadMb(): number {
  return RISK_ANALYSIS_MAX_UPLOAD_BYTES / (1024 * 1024);
}

export function validateRiskAnalysisUploadSize(file: File): string | null {
  if (file.size > RISK_ANALYSIS_MAX_UPLOAD_BYTES) {
    return `File exceeds the maximum allowed size of ${formatRiskAnalysisMaxUploadMb()}MB`;
  }
  return null;
}

async function pollSurveyAnalysisJob(
  jobId: string,
): Promise<AnalyzeSurveyResult> {
  for (let attempt = 0; attempt < SURVEY_POLL_MAX_ATTEMPTS; attempt += 1) {
    const response = await authenticatedFetch(
      `${BASE_URL}${ANALYZE_SURVEY_JOB_ENDPOINT}/${jobId}`,
      { method: "GET" },
    );
    if (!response.ok) {
      const apiErrorMessage = await getApiErrorMessage(response);
      throw new Error(apiErrorMessage || "Survey analysis job poll failed");
    }
    const statusBody = (await response.json()) as SurveyJobStatusResponse;
    if (statusBody.status === "complete" && statusBody.result) {
      return statusBody.result;
    }
    if (statusBody.status === "failed") {
      throw new Error(
        (statusBody.error && statusBody.error.trim()) ||
          "Survey analysis failed",
      );
    }
    if (attempt < SURVEY_POLL_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) =>
        setTimeout(resolve, SURVEY_POLL_INTERVAL_MS),
      );
    }
  }
  throw new Error(
    "Survey analysis timed out while waiting for results. Please try again.",
  );
}

/**
 * Queue survey OCR+LLM analysis and poll until complete (ACA ingress-safe).
 */
async function analyzeSurveyDocumentAsync(
  file: File,
  facilityId?: string,
): Promise<AnalyzeSurveyResult> {
  if (!facilityId) {
    throw new Error("Facility ID is required for survey analysis.");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("facility_id", facilityId);

  const startResponse = await authenticatedFetch(
    `${BASE_URL}${ANALYZE_SURVEY_ASYNC_ENDPOINT}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!startResponse.ok) {
    const apiErrorMessage = await getApiErrorMessage(startResponse);
    throw new Error(apiErrorMessage || "Failed to start survey analysis");
  }

  const started = (await startResponse.json()) as SurveyJobStartResponse;
  if (!started?.job_id) {
    throw new Error("Survey analysis job id missing from server response");
  }
  return pollSurveyAnalysisJob(started.job_id);
}

async function pollDocumentAnalysisJob(
  jobId: string,
): Promise<Record<string, unknown>> {
  for (let attempt = 0; attempt < DOCUMENT_POLL_MAX_ATTEMPTS; attempt += 1) {
    const response = await authenticatedFetch(
      `${BASE_URL}${ANALYZE_DOCUMENT_JOB_ENDPOINT}/${jobId}`,
      { method: "GET" },
    );
    if (!response.ok) {
      const apiErrorMessage = await getApiErrorMessage(response);
      throw new Error(apiErrorMessage || "Document analysis job poll failed");
    }
    const statusBody = (await response.json()) as DocumentJobStatusResponse;
    if (statusBody.status === "complete" && statusBody.result) {
      return statusBody.result;
    }
    if (statusBody.status === "failed") {
      throw new Error(
        (statusBody.error && statusBody.error.trim()) ||
          "Document analysis failed",
      );
    }
    if (attempt < DOCUMENT_POLL_MAX_ATTEMPTS - 1) {
      await new Promise((resolve) =>
        setTimeout(resolve, DOCUMENT_POLL_INTERVAL_MS),
      );
    }
  }
  throw new Error(
    "Document analysis timed out while waiting for results. Please try again.",
  );
}

/**
 * Queue incident OCR+LLM analysis and poll until complete (ACA ingress-safe).
 */
async function analyzeDocumentAsync(
  payload: AnalyzeDocumentPayload,
): Promise<Record<string, unknown>> {
  if (!payload.facility_id) {
    throw new Error("Facility ID is required for document analysis.");
  }
  if (payload.document_type !== "incident_report") {
    throw new Error(
      "Async document analysis currently supports incident_report only.",
    );
  }

  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("document_type", payload.document_type);
  formData.append("industry", payload.industry);
  formData.append("analysis_type", payload.analysis_type);
  formData.append("facility_id", payload.facility_id);

  const startResponse = await authenticatedFetch(
    `${BASE_URL}${ANALYZE_DOCUMENT_ASYNC_ENDPOINT}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!startResponse.ok) {
    const apiErrorMessage = await getApiErrorMessage(startResponse);
    throw new Error(apiErrorMessage || "Failed to start document analysis");
  }

  const started = (await startResponse.json()) as DocumentJobStartResponse;
  if (!started?.job_id) {
    throw new Error("Document analysis job id missing from server response");
  }
  return pollDocumentAnalysisJob(started.job_id);
}

async function getApiErrorMessage(response: Response): Promise<string> {
  const fallback = `${response.status} ${response.statusText}`.trim();

  if (response.status === 413) {
    return `File is too large to upload. Maximum allowed size is ${formatRiskAnalysisMaxUploadMb()}MB.`;
  }

  try {
    const errBody = await response.json();
    const detail = errBody?.detail;

    if (typeof detail === "string" && detail.trim()) {
      return detail.trim();
    }

    if (detail != null && typeof detail === "object" && !Array.isArray(detail)) {
      const detailObj = detail as Record<string, unknown>;
      const code =
        typeof detailObj.code === "string" ? detailObj.code.trim() : "";
      if (typeof detailObj.message === "string" && detailObj.message.trim()) {
        return detailObj.message.trim();
      }
      if (code === "ocr_not_enabled") {
        return (
          "Document OCR is not enabled for your account. " +
          "Contact your administrator or support to enable OCR on your plan."
        );
      }
      if (code === "insufficient_ocr_pages") {
        return (
          "Your OCR page allowance is exhausted. " +
          "Contact your administrator or support to purchase additional OCR pages."
        );
      }
      if (typeof detailObj.detail === "string" && detailObj.detail.trim()) {
        return detailObj.detail.trim();
      }
    }

    if (typeof errBody?.message === "string" && errBody.message.trim()) {
      return errBody.message.trim();
    }
  } catch {
    // Ignore JSON parsing errors and return fallback message.
  }

  return fallback;
}
export const riskAnalysisService = {
  /**
   * Analyze employment agreement packages (multiple PDF/DOCX/TXT allowed per request).
   */
  async analyzeEmployeeAgreement(
    files: File[],
    facilityId: string,
    employeeType: string = "physician",
  ): Promise<EmployeeAgreementAnalysisResponse> {
    if (!files.length) {
      throw new Error("Select at least one document.");
    }
    // Non-stream path uses the same Documents SSE endpoint and waits for complete.
    let streamError: EmployeeAgreementRequestError | null = null;
    const result = await this.analyzeEmployeeAgreementStream(
      files,
      facilityId,
      employeeType,
      {
        onFileResult: () => undefined,
        onComplete: () => undefined,
        onError: (message, fileResults) => {
          streamError = new EmployeeAgreementRequestError(message, fileResults);
        },
      },
    );
    if (streamError) {
      throw streamError;
    }
    if (!result) {
      throw new EmployeeAgreementRequestError("Employee agreement analysis failed.");
    }
    return result;
  },

  /**
   * Stream extraction + analysis over SSE: each file's OCR/text is available via onFileResult
   * before later files finish; final payload matches analyzeEmployeeAgreement.
   */
  async analyzeEmployeeAgreementStream(
    files: File[],
    facilityId: string,
    employeeType: string,
    callbacks: {
      onFileExtracting?: (
        filename: string,
        meta?: FileExtractingMeta,
      ) => void;
      onFilePageProgress?: (ev: FilePageProgressEvent) => void;
      onFileResult: (fileResult: EmployeeAgreementFileResult) => void;
      onComplete: (response: EmployeeAgreementAnalysisResponse) => void;
      onError: (
        message: string,
        fileResults?: EmployeeAgreementFileResult[],
      ) => void;
    },
  ): Promise<EmployeeAgreementAnalysisResponse | null> {
    if (!files.length) {
      throw new Error("Select at least one document.");
    }
    const ordered = sortMultifileAnalysisUploadsByName(files);
    const formData = new FormData();
    for (const f of ordered) {
      formData.append("files", f);
    }
    appendEmploymentPackageFormFields(formData, facilityId, employeeType);

    const response = await authenticatedFetch(
      `${DOCUMENTS_BASE_URL}${ANALYZE_EMPLOYEE_AGREEMENT_STREAM_ENDPOINT}`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`;
      let fileResults: EmployeeAgreementFileResult[] | undefined;
      try {
        const errBody = await response.json();
        const d = errBody?.detail;
        if (d != null && typeof d === "object" && !Array.isArray(d)) {
          const obj = d as Record<string, unknown>;
          if (typeof obj.message === "string") {
            message = obj.message;
          }
          if (Array.isArray(obj.file_results)) {
            fileResults = obj.file_results as EmployeeAgreementFileResult[];
          }
        } else if (typeof d === "string") {
          message = d;
        }
      } catch {
        /* ignore */
      }
      callbacks.onError(message, fileResults);
      return null;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError("No response body from analysis stream.");
      return null;
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let completed: EmployeeAgreementAnalysisResponse | null = null;

    const handleEventData = (jsonStr: string) => {
      let obj: Record<string, unknown>;
      try {
        obj = JSON.parse(jsonStr) as Record<string, unknown>;
      } catch {
        return;
      }
      const ev = obj.event;
      if (ev === "file_extracting" && typeof obj.filename === "string") {
        const meta: FileExtractingMeta = {
          pdf: Boolean(obj.pdf),
          extractionHint:
            typeof obj.extraction_hint === "string"
              ? obj.extraction_hint
              : undefined,
        };
        callbacks.onFileExtracting?.(obj.filename, meta);
        return;
      }
      if (ev === "file_page_progress") {
        const fn = typeof obj.filename === "string" ? obj.filename : "";
        const page =
          typeof obj.page === "number" ? obj.page : Number(obj.page);
        const totalPages =
          typeof obj.total_pages === "number"
            ? obj.total_pages
            : Number(obj.total_pages);
        if (
          fn &&
          Number.isFinite(page) &&
          Number.isFinite(totalPages) &&
          totalPages > 0
        ) {
          callbacks.onFilePageProgress?.({
            filename: fn,
            page,
            totalPages,
          });
        }
        return;
      }
      if (ev === "file_result" && obj.file_result != null) {
        callbacks.onFileResult(obj.file_result as EmployeeAgreementFileResult);
        return;
      }
      if (ev === "complete") {
        const payload: EmployeeAgreementAnalysisResponse = {
          success: Boolean(obj.success),
          message:
            typeof obj.message === "string" ? obj.message : undefined,
          analysis: (obj.analysis ?? {}) as Record<string, unknown>,
          metadata: (obj.metadata ?? {}) as EmployeeAgreementAnalysisResponse["metadata"],
        };
        completed = payload;
        callbacks.onComplete(payload);
        return;
      }
      if (ev === "error") {
        const d = obj.detail as Record<string, unknown> | undefined;
        const msg =
          d != null && typeof d.message === "string"
            ? d.message
            : "Analysis failed";
        const fr =
          d != null && Array.isArray(d.file_results)
            ? (d.file_results as EmployeeAgreementFileResult[])
            : undefined;
        callbacks.onError(msg, fr);
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          for (const line of chunk.split("\n")) {
            const t = line.trim();
            if (t.startsWith("data:")) {
              const payload = t.slice(5).trim();
              if (payload) {
                handleEventData(payload);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
    return completed;
  },

  async analyzePayorContractStream(
    files: File[],
    facilityId: string,
    context: {
      country?: string;
      states?: string;
      facilityTypes?: string;
    },
    callbacks: {
      onFileExtracting?: (
        filename: string,
        meta?: FileExtractingMeta,
      ) => void;
      onFilePageProgress?: (ev: FilePageProgressEvent) => void;
      onFileResult: (fileResult: EmployeeAgreementFileResult) => void;
      onComplete: (response: PayorContractAnalysisResponse) => void;
      onError: (
        message: string,
        fileResults?: EmployeeAgreementFileResult[],
      ) => void;
    },
  ): Promise<PayorContractAnalysisResponse | null> {
    if (!files.length) {
      throw new Error("Select at least one document.");
    }
    const ordered = sortMultifileAnalysisUploadsByName(files);
    const formData = new FormData();
    for (const f of ordered) {
      formData.append("files", f);
    }
    formData.append("facility_id", facilityId);
    formData.append("country", context.country ?? "");
    formData.append("states", context.states ?? "");
    formData.append("facility_types", context.facilityTypes ?? "");

    const response = await authenticatedFetch(
      `${BASE_URL}${ANALYZE_PAYOR_CONTRACT_STREAM_ENDPOINT}`,
      {
        method: "POST",
        body: formData,
      },
    );

    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`;
      let fileResults: EmployeeAgreementFileResult[] | undefined;
      try {
        const errBody = await response.json();
        const d = errBody?.detail;
        if (d != null && typeof d === "object" && !Array.isArray(d)) {
          const obj = d as Record<string, unknown>;
          if (typeof obj.message === "string") {
            message = obj.message;
          }
          if (Array.isArray(obj.file_results)) {
            fileResults = obj.file_results as EmployeeAgreementFileResult[];
          }
        } else if (typeof d === "string") {
          message = d;
        }
      } catch {
        /* ignore */
      }
      callbacks.onError(message, fileResults);
      return null;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError("No response body from analysis stream.");
      return null;
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let completed: PayorContractAnalysisResponse | null = null;

    const handleEventData = (jsonStr: string) => {
      let obj: Record<string, unknown>;
      try {
        obj = JSON.parse(jsonStr) as Record<string, unknown>;
      } catch {
        return;
      }
      const ev = obj.event;
      if (ev === "file_extracting" && typeof obj.filename === "string") {
        const meta: FileExtractingMeta = {
          pdf: Boolean(obj.pdf),
          extractionHint:
            typeof obj.extraction_hint === "string"
              ? obj.extraction_hint
              : undefined,
        };
        callbacks.onFileExtracting?.(obj.filename, meta);
        return;
      }
      if (ev === "file_page_progress") {
        const fn = typeof obj.filename === "string" ? obj.filename : "";
        const page =
          typeof obj.page === "number" ? obj.page : Number(obj.page);
        const totalPages =
          typeof obj.total_pages === "number"
            ? obj.total_pages
            : Number(obj.total_pages);
        if (
          fn &&
          Number.isFinite(page) &&
          Number.isFinite(totalPages) &&
          totalPages > 0
        ) {
          callbacks.onFilePageProgress?.({
            filename: fn,
            page,
            totalPages,
          });
        }
        return;
      }
      if (ev === "file_result" && obj.file_result != null) {
        callbacks.onFileResult(obj.file_result as EmployeeAgreementFileResult);
        return;
      }
      if (ev === "complete") {
        const payload: PayorContractAnalysisResponse = {
          success: Boolean(obj.success),
          message:
            typeof obj.message === "string" ? obj.message : undefined,
          analysis: (obj.analysis ?? {}) as Record<string, unknown>,
          metadata: (obj.metadata ?? {}) as PayorContractAnalysisResponse["metadata"],
        };
        completed = payload;
        callbacks.onComplete(payload);
        return;
      }
      if (ev === "error") {
        const d = obj.detail as Record<string, unknown> | undefined;
        const msg =
          d != null && typeof d.message === "string"
            ? d.message
            : "Analysis failed";
        const fr =
          d != null && Array.isArray(d.file_results)
            ? (d.file_results as EmployeeAgreementFileResult[])
            : undefined;
        callbacks.onError(msg, fr);
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          for (const line of chunk.split("\n")) {
            const t = line.trim();
            if (t.startsWith("data:")) {
              const payload = t.slice(5).trim();
              if (payload) {
                handleEventData(payload);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
    return completed;
  },

  async analyzeDocument(
    payload: AnalyzeDocumentPayload,
  ): Promise<RiskAnalysisResponse> {
    try {
      // Incident reports use async job + poll to avoid ACA ~240s 504.
      // survey_document path kept via analyzeSurvey / uploadSurveyDocument.
      const raw = await analyzeDocumentAsync(payload);
      const normalized = normalizeRiskAnalysisResponse(raw);
      return mergeLlmDebugFields(normalized, raw);
    } catch (error) {
      console.error("Risk analysis API error:", error);
      throw error;
    }
  },

  /**
   * Analyze a survey document via async job + poll (ACA ingress-safe).
   * Payload: multipart/form-data with 'file' and 'facility_id'.
   */
  async analyzeSurvey(
    file: File,
    facility_id?: string,
  ): Promise<RiskAnalysisResponse> {
    const raw = await analyzeSurveyDocumentAsync(file, facility_id);

    // If backend includes full_analysis similar to previous schema, normalize that.
    if (raw?.full_analysis) {
      const normalized = normalizeRiskAnalysisResponse({
        success: true,
        message: "Survey analysis completed",
        analysis: raw.full_analysis,
        metadata: {
          filename: file.name,
          file_size: file.size,
          document_type: "survey_document",
          analysis_timestamp: new Date().toISOString(),
        },
      });

      // Also propagate deficiency_tags and survey_date if present at top-level
      try {
        const deficiencyTags = raw?.full_analysis?.deficiency_tags;
        if (Array.isArray(deficiencyTags)) {
          (normalized as any).analysis.deficiency_tags = deficiencyTags;
        }
        if (raw?.survey_date) {
          (normalized as any).analysis.survey_metadata = {
            ...(normalized as any).analysis.survey_metadata,
            survey_date: raw.survey_date,
          };
        }
      } catch {}

      return mergeLlmDebugFields(normalized, raw);
    }

    // Minimal fallback mapping from simplified response
    const tags = Array.isArray(raw?.deficiency_descriptions)
      ? raw.deficiency_descriptions.map((d: any) => ({
          tag: d?.tag || "",
          description: d?.description || "",
          severity: "MEDIUM",
          category: "",
          page_reference: "",
        }))
      : [];

    const mapped: RiskAnalysisResponse = {
      success: true,
      message: "Survey analysis completed",
      analysis: {
        document_type: "survey_document",
        analysis_date: new Date().toISOString().slice(0, 10),
        facility_info: {
          industry: "Healthcare",
          facility_type: raw?.facility_type || "Unknown Facility",
        },
        incident_summary: {
          narrative: "Survey analysis",
          incident_type: "Survey",
          clinical_classification: "N/A",
          date: raw?.survey_date || "",
          location: "",
          description: "",
          parties_involved: [],
          injuries_damages: "",
          environmental_factors: "",
          reporting_timeliness: "",
          documentation_completeness: "",
        },
        compliance_deficiencies: [],
        potential_penalties: {
          penalty_table: [],
          penalty_charts: {
            estimated_penalty_exposure: {
              min: "0",
              max: "0",
              most_likely: "0",
            },
            estimated_corrective_investment: {
              immediate: "0",
              long_term: "0",
              total: "0",
            },
          },
        },
        plan_of_correction: {
          action_table: [],
          documentation_improvements: [],
          training_requirements: [],
          system_improvements: [],
          corrective_costs: {
            immediate_costs: "0",
            long_term_costs: "0",
            total_estimated_cost: "0",
          },
          monitoring_tracking: [],
        },
        risk_score: {
          overall_score: 0,
          score_breakdown: {
            documentation: 0,
            compliance: 0,
            safety: 0,
            financial_risk: 0,
          },
          risk_level: "LOW",
          issues_count: tags.length,
          actions_count: 0,
          justification: "Derived from analyze-survey response",
        },
        executive_summary: "",
        recommendations: { recommendation_table: [] },
      },
      metadata: {
        filename: file.name,
        file_size: file.size,
        document_type: "survey_document",
        analysis_timestamp: new Date().toISOString(),
      },
    };

    // Attach survey-specific fields used by UI
    (mapped as any).analysis.survey_metadata = {
      surveying_body: "CMS",
      survey_date: raw?.survey_date || "",
      facility_name: "",
      survey_type: "Survey",
    };
    const fullAnalysisTags = raw?.full_analysis?.deficiency_tags;
    (mapped as any).analysis.deficiency_tags = Array.isArray(fullAnalysisTags)
      ? fullAnalysisTags
      : Array.isArray(raw?.deficiency_descriptions)
        ? raw.deficiency_descriptions.map((d: any) => ({
            tag: d?.tag,
            description: d?.description,
            severity: "MEDIUM",
            category: "",
            page_reference: "",
          }))
        : [];

    return mapped;
  },

  /**
   * Lookup CMS SOM requirements (e.g., J-tags) via vector store search.
   * Falls back to access token from localStorage if auth_token not provided.
   */
  async somSearch(payload: SomSearchPayload): Promise<SomSearchResponse> {
    const defaults: SomSearchPayload = {
      capture_test_data: false,
      snapshots_size: 20,
      user_persona: "survey_analyst",
      org_id: "",
      org_name: "test",
      customer_id: "1",
      category: "VectorStores_usa_healthcare_manual_som107ap_g_rhc",
      query: "",
    };
    const token = localStorage.getItem("access_token") || "";
    const body: SomSearchPayload = {
      ...defaults,
      ...payload,
      auth_token: payload.auth_token ?? token,
    };

    const resp = await authenticatedFetch(
      `${BASE_URL}${SOM_SEARCH_URL_ENDPOINT}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    if (!resp.ok) {
      throw new Error(`SOM search failed: ${resp.status} ${resp.statusText}`);
    }

    const data = (await resp.json()) as SomSearchResponse;
    return data;
  },
};

// --- Normalization helpers ---
export const RISK_LLM_SECTION = {
  surveyReport: "survey_report",
  incidentReport: "incident_report",
  surveyDocument: "survey_document",
} as const;

function mergeLlmDebugFields(
  target: RiskAnalysisResponse,
  raw: Record<string, unknown> | null | undefined,
): RiskAnalysisResponse {
  if (!raw || typeof raw !== "object") {
    return target;
  }
  const prompts = raw.llmPromptsBySection;
  if (prompts && typeof prompts === "object") {
    target.llmPromptsBySection = {
      ...target.llmPromptsBySection,
      ...(prompts as Record<string, string>),
    };
  }
  const walls = raw.llmInvokeWallSecondsBySection;
  if (walls && typeof walls === "object") {
    target.llmInvokeWallSecondsBySection = {
      ...target.llmInvokeWallSecondsBySection,
      ...(walls as Record<string, number>),
    };
  }
  return target;
}

function normalizeRiskAnalysisResponse(raw: any): RiskAnalysisResponse {
  // If it already matches our schema shape, return as-is
  if (raw && raw.analysis && raw.metadata && typeof raw.analysis === "object") {
    const docType = raw.metadata?.document_type || raw.analysis?.document_type;
    if (docType !== "survey_document") {
      return mergeLlmDebugFields(raw as RiskAnalysisResponse, raw);
    }
  }

  // Handle survey_document alternate shape (sample provided by backend)
  const a = raw?.analysis || {};
  const meta = raw?.metadata || {};

  const facilityDetected =
    a?.facility_type?.detected_type || "Unknown Facility";
  const surveyingBody = a?.survey_metadata?.surveying_body || "Unknown";
  const surveyType = a?.survey_metadata?.survey_type || "Survey";
  const surveyDate = a?.survey_metadata?.survey_date || a?.analysis_date || "";
  const facilityName = a?.survey_metadata?.facility_name || "Facility";

  // Map deficiency tags into our compliance_deficiencies shape
  const deficiencies = Array.isArray(a?.deficiency_tags)
    ? a.deficiency_tags.map((d: any) => ({
        category: d?.category || "General",
        requirement: d?.description || d?.tag || "Requirement",
        regulatory_source: surveyingBody,
        specific_regulation_citation: d?.tag || "",
        official_url: "",
        deficiency_description: d?.description || "",
        risk_level: (d?.severity || "MEDIUM").toUpperCase(),
        associated_risks: [d?.category || ""],
      }))
    : [];

  const recs = Array.isArray(a?.compliance_gaps?.recommendations)
    ? a.compliance_gaps.recommendations
    : [];

  const actions = Array.isArray(a?.compliance_gaps?.priority_actions)
    ? a.compliance_gaps.priority_actions
    : [];

  const narrative = `Survey performed by ${surveyingBody} on ${surveyDate} for ${facilityName}.`;

  const normalized: RiskAnalysisResponse = {
    success: !!raw?.success,
    message: raw?.message || "Analysis completed",
    analysis: {
      document_type: "survey_document",
      analysis_date: a?.analysis_date || new Date().toISOString().slice(0, 10),
      facility_info: {
        industry:
          raw?.metadata?.document_type === "survey_document"
            ? "Healthcare"
            : raw?.analysis?.facility_info?.industry || "Healthcare",
        facility_type: facilityDetected,
      },
      incident_summary: {
        narrative,
        incident_type: surveyType,
        clinical_classification: "N/A",
        date: surveyDate || "",
        location: facilityName,
        description: a?.survey_summary?.key_findings || "",
        parties_involved: [],
        injuries_damages: "",
        environmental_factors: "",
        reporting_timeliness: "",
        documentation_completeness: "",
      },
      compliance_deficiencies: deficiencies,
      potential_penalties: {
        penalty_table: [],
        penalty_charts: {
          estimated_penalty_exposure: { min: "0", max: "0", most_likely: "0" },
          estimated_corrective_investment: {
            immediate: "0",
            long_term: "0",
            total: "0",
          },
        },
      },
      plan_of_correction: {
        action_table: actions.map((a: string) => ({
          action: a,
          priority: "HIGH",
          timeline: "30-60 days",
          responsible: "Compliance",
        })),
        documentation_improvements: [],
        training_requirements: [],
        system_improvements: [],
        corrective_costs: {
          immediate_costs: "0",
          long_term_costs: "0",
          total_estimated_cost: "0",
        },
        monitoring_tracking: [],
      },
      risk_score: {
        overall_score: 0,
        score_breakdown: {
          documentation: 0,
          compliance: 0,
          safety: 0,
          financial_risk: 0,
        },
        risk_level: "LOW",
        issues_count: deficiencies.length,
        actions_count: actions.length,
        justification: "Derived from survey_document mapping",
      },
      executive_summary: a?.survey_summary?.key_findings || "",
      recommendations: {
        recommendation_table: recs.map((r: string) => ({
          recommendation: r,
          priority: "HIGH",
          rationale: "Survey gap",
          estimated_cost: "TBD",
          expected_outcome: "Improved compliance",
        })),
      },
    },
    metadata: {
      filename: meta?.filename || "uploaded_document",
      file_size: Number(meta?.file_size) || 0,
      document_type: meta?.document_type || "survey_document",
      analysis_timestamp: meta?.analysis_timestamp || new Date().toISOString(),
    },
  };

  // Attach optional survey-specific fields without breaking typings
  try {
    const surveyMeta = a?.survey_metadata || null;
    const surveySummary = a?.survey_summary || null;
    const validationWarning =
      a?.validation_warning || raw?.analysis?.validation_warning || null;
    const validationErrors =
      a?.validation_errors || raw?.analysis?.validation_errors || null;
    const deficiencyTags = Array.isArray(a?.deficiency_tags)
      ? a.deficiency_tags
      : null;

    if (surveyMeta) {
      (normalized as any).analysis.survey_metadata = surveyMeta;
    }
    if (surveySummary) {
      (normalized as any).analysis.survey_summary = surveySummary;
    }
    if (validationWarning) {
      (normalized as any).analysis.validation_warning = validationWarning;
    }
    if (validationErrors) {
      (normalized as any).analysis.validation_errors = validationErrors;
    }
    if (deficiencyTags) {
      (normalized as any).analysis.deficiency_tags = deficiencyTags;
    }
  } catch (_) {
    // no-op: best-effort enrichment
  }

  return mergeLlmDebugFields(normalized, raw);
}

// Survey Report Service Methods
export const surveyReportService = {
  /**
   * Fetch survey reports for a facility
   */
  async getSurveyReports(facilityId?: string): Promise<SurveyReportResponse> {
    try {
      const url = facilityId
        ? `${BASE_URL}${SURVEY_REPORT_ENDPOINT}?facilityId=${facilityId}`
        : `${BASE_URL}${SURVEY_REPORT_ENDPOINT}`;

      const response = await authenticatedFetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch survey reports: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      return data as SurveyReportResponse;
    } catch (error) {
      console.error("Survey reports API error:", error);
      throw error;
    }
  },

  /**
   * Upload survey document for analysis (async job + poll; ACA ingress-safe).
   */
  async uploadSurveyDocument(
    payload: UploadSurveyDocumentPayload,
  ): Promise<AnalyzeSurveyResult> {
    console.log("🌐 uploadSurveyDocument - async survey analysis");
    console.log("🌐 URL:", `${BASE_URL}${ANALYZE_SURVEY_ASYNC_ENDPOINT}`);
    console.log("🌐 Payload:", {
      fileName: payload.file.name,
      fileSize: payload.file.size,
      documentType: payload.documentType,
      facilityId: payload.facilityId,
    });

    try {
      const data = await analyzeSurveyDocumentAsync(
        payload.file,
        payload.facilityId,
      );
      console.log("✅ Survey analysis job complete:", data);
      return data;
    } catch (error) {
      console.error("Upload survey document API error:", error);
      throw error;
    }
  },

  /**
   * Get a specific survey report by tag (e.g., "J0042")
   */
  async getSurveyReportByTag(
    tag: string,
    facilityId?: string,
  ): Promise<SurveyReportItem> {
    try {
      const url = facilityId
        ? `${BASE_URL}${SURVEY_REPORT_ENDPOINT}/${tag}?facilityId=${facilityId}`
        : `${BASE_URL}${SURVEY_REPORT_ENDPOINT}/${tag}`;

      const response = await authenticatedFetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch survey report: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      return data as SurveyReportItem;
    } catch (error) {
      console.error("Survey report by tag API error:", error);
      throw error;
    }
  },

  /**
   * Download survey report as PDF
   */
  async downloadSurveyReport(
    facilityId?: string,
    tags?: string[],
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      if (facilityId) params.append("facilityId", facilityId);
      if (tags && tags.length > 0) {
        tags.forEach((tag) => params.append("tags", tag));
      }

      const url = `${BASE_URL}${SURVEY_REPORT_ENDPOINT}/download?${params.toString()}`;

      const response = await authenticatedFetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`,
        );
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error("Download survey report API error:", error);
      throw error;
    }
  },
};
